import { useCallback, useMemo, useState } from 'react';

import { EvChipItem, EvComboboxOption } from '@evinced-private/ui-common';

import { useGetTenantUsers } from 'src/hooks/queries/useGetTenantUsers';
import { getActiveTenantUsers } from 'src/services/TenantsService';

import { DEFAULT_PAGE_SIZE, SEARCH_PAGE_SIZE } from './EmailSettings.constants';
import { mapUsersToOptions } from './EmailSettings.utils';

interface UseEmailRecipientsReturn {
	recipients: EvChipItem[];
	initialOptions: EvComboboxOption[];
	loadUserOptions: (searchText: string, cb: (options: EvComboboxOption[]) => void) => void;
	handleRecipientSelect: (selected: EvComboboxOption[]) => void;
	handleRemoveRecipient: (chipId: string) => void;
}

export const useEmailRecipients = (): UseEmailRecipientsReturn => {
	const [recipients, setRecipients] = useState<EvChipItem[]>([]);

	const disabledIds = useMemo(() => new Set(recipients.map((r) => r.id)), [recipients]);

	const { data: initialUsers } = useGetTenantUsers();
	const initialOptions = useMemo(
		() => (initialUsers ? mapUsersToOptions(initialUsers, disabledIds) : []),
		[initialUsers, disabledIds]
	);

	const loadUserOptions = useCallback(
		(searchText: string, cb: (options: EvComboboxOption[]) => void): void => {
			const pageSize = searchText ? SEARCH_PAGE_SIZE : DEFAULT_PAGE_SIZE;
			getActiveTenantUsers(searchText, pageSize).then((users) => {
				cb(mapUsersToOptions(users, disabledIds));
			});
		},
		[disabledIds]
	);

	const handleRecipientSelect = useCallback((selected: EvComboboxOption[]): void => {
		if (!selected?.[0]) {
			return;
		}
		const option = selected[0];
		setRecipients((prev) => {
			if (prev.some((r) => r.id === String(option.value))) {
				return prev;
			}
			return [...prev, { id: String(option.value), label: option.label }];
		});
	}, []);

	const handleRemoveRecipient = useCallback((chipId: string): void => {
		setRecipients((prev) => prev.filter((r) => r.id !== chipId));
	}, []);

	return {
		recipients,
		initialOptions,
		loadUserOptions,
		handleRecipientSelect,
		handleRemoveRecipient
	};
};
